/**
 * The decision view (docs/ux/project-tab-spec.md §1.6): a decision is never only a one-tap card.
 * Header (full question, who asked, lane and task, category, leverage, what happens if you skip
 * it) · options as a numbered ballot (rationale, trade-offs, reversibility, Suggested; revisions
 * with a diff) · evidence (diffs, tests, commits, artifacts, the transcript around the question;
 * every reference openable) · the thread with the asking agent · the answer bar (pick, answer in
 * my words, decide for me with guidance, later). Presentational: the container wires actions.
 */
import { Link, useNavigate } from '@tanstack/react-router'
import type { BarkAttachment } from '@viko/protocol'
import { Button, Markdown } from '@viko/transcript-ui'
import {
  ArrowRight,
  Check,
  ChevronRight,
  Copy,
  FileDiff,
  FlaskConical,
  GitCommitHorizontal,
  Image as ImageIcon,
  Info,
  Link2,
  Mic,
  MicOff,
  MoreHorizontal,
  ScrollText,
  Send,
  Sparkles,
} from 'lucide-react'
import { type ReactNode, useEffect, useId, useMemo, useRef, useState } from 'react'
import { type ActionCtx, type ActionEntity, actions } from '../../lib/actions.js'
import { LARGE_TEXT_BYTES, useAttachmentInput } from '../../lib/attachment-input.js'
import {
  type DecisionView as DecisionModel,
  deliveryText,
  type EvidenceView,
  type OptionView,
  optionDiff,
} from '../../lib/decision-view.js'
import { SNOOZE_OPTIONS, snoozeUntil } from '../../lib/inbox.js'
import { useActionKeys, useSelectionActions } from '../../lib/selection-actions.js'
import { useDictation } from '../../lib/speech.js'
import type { PendingFile } from '../../lib/uploads.js'
import { AttachmentDropZone } from '../AttachmentDropZone.js'
import { useAppUi } from '../app-ui.js'
import { storage } from '../Capture.js'
import { Keycap } from '../inspector/ActionFooter.js'
import { Ago, useToast } from '../ui.js'
import { useWide } from '../useWide.js'
import { OptionBoard, type RenderMedia, useBoardState } from './OptionBoard.js' // visual-review
import {
  moveOnArrow,
  type Note,
  NoteField,
  type OptionCursor,
  OptionKey,
  optionState,
  SuggestedPill,
  useNote,
  useOptionCursor,
} from './OptionList.js'
import './decision.css'

export interface DecisionContext {
  projectName?: string
  lane?: string
  agentLabel?: string
  /** The one-line form ("Claude on Mac Studio") for the compact meta line. */
  agentShort?: string
  task?: { title: string; onOpen?: () => void }
  /** "Open full conversation" in the canonical viewer. */
  conversationRunId?: string
  /** ia-shell: "Open in session" (⇧V) — the run in its project's chrome, at this ask. */
  conversationHref?: string
}

/** ia-shell: a pick's optional note and files (Claude "add notes" parity). */
export interface PickExtra {
  note?: string
  files?: PendingFile[]
  // ---- visual-review: a mix per area and per-option comments ----
  mix?: { area: string; optionId: string }[]
  comments?: Record<string, string>
  // ---- end visual-review ----
}

export interface DecisionViewProps {
  view: DecisionModel
  context: DecisionContext
  onPick: (optionId: string, extra?: PickExtra) => Promise<void>
  onFreeform: (text: string) => Promise<void>
  onDelegate: (guidance?: string) => Promise<void>
  onSnooze: (until: number) => Promise<void>
  /** Absent for legacy asks ("Reply in conversation"). */
  onMessage?: (text: string, files: PendingFile[]) => Promise<void>
  /** Renders one evidence reference as an openable link (path/commit resolve on the run's machine). */
  renderRef?: (e: EvidenceView, children: ReactNode) => ReactNode
  renderArtifact?: (e: EvidenceView) => ReactNode
  /** visual-review: one image / HTML board of a design option (decrypted on this device). */
  renderMedia?: RenderMedia
  /** The transcript around the question (embedded tail). */
  transcript?: ReactNode
  /**
   * ia-shell: "Turns that led here" (decision 5): the turns behind the ask, rendered by the
   * container. `expanded` is driven by `v`; `all` by "Show N earlier turns".
   */
  turns?: (o: { expanded: boolean; setExpanded: (x: boolean) => void }) => ReactNode
  /** ia-shell: n / v / ⇧V inside this view (off when several views are mounted). */
  hotkeys?: boolean
  /** Run artifacts gallery. */
  artifacts?: ReactNode
  /** Show the title as this view's heading (the Lanes inspector has its own). */
  showTitle?: boolean
  /** Open with a panel ready: `reply` (r), `delegate` (d), `freeform`. */
  intent?: 'reply' | 'delegate' | 'freeform'
  /** Handle 1–9 / s here (standalone views; lists handle their own keys). */
  keys?: boolean
  busy?: boolean
}

type Mode = 'pick' | 'delegate' | 'later'
/** What the field under the options sends (app-clarity "Composer modes"). */
export type Compose = 'note' | 'answer' | 'request'

export function DecisionView(p: DecisionViewProps) {
  const { view: v } = p
  const id = useId()
  // The keyboard cursor starts on the suggested option; nothing is picked until you pick.
  const cur = useOptionCursor(v.options, v.suggested?.id, `${v.key}:${v.revision.n}`)
  const selected = cur.target
  const [mode, setMode] = useState<Mode>(p.intent === 'delegate' ? 'delegate' : 'pick')
  const [busy, setBusy] = useState(false)
  const composer = useRef<HTMLTextAreaElement>(null)
  // ---- ia-shell: the note that rides with a pick, and the turns section ----
  const note = useNote()
  const board = useBoardState(v) // visual-review
  // core-loop: the context is open by default in the inspector (decision 5: read and answer in one
  // place); a batch of expanded decisions keeps each collapsed. `v` folds it.
  // app-clarity: "How it got here" is collapsed by default (v opens it); the options stay docked.
  const turnsDefault = false
  const [turnsOpen, setTurnsOpen] = useState(turnsDefault)
  const [compose, setCompose] = useState<Compose>(p.intent === 'freeform' ? 'answer' : 'note')
  const wide = useWide()
  const ui = useAppUi()
  const [details, setDetails] = useState(false)
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset per decision
  useEffect(() => {
    setTurnsOpen(turnsDefault)
    setDetails(false)
  }, [v.key])
  const hot = p.hotkeys !== false && v.open
  const nav = useNavigate()
  // ---- end ia-shell ----
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-seed per decision
  useEffect(() => {
    setMode(p.intent === 'delegate' ? 'delegate' : 'pick')
    setCompose(p.intent === 'freeform' ? 'answer' : 'note')
    note.clear()
  }, [v.key, p.intent])
  // biome-ignore lint/correctness/useExhaustiveDependencies: focus once per intent
  useEffect(() => {
    if (p.intent === 'reply') composer.current?.focus()
    // "Answer in my words" is the note: it is always there, so the intent just focuses it.
    if (p.intent === 'freeform') note.focus()
  }, [p.intent])

  const act = async (fn: () => Promise<void>) => {
    if (busy) return
    setBusy(true)
    try {
      await fn()
    } finally {
      setBusy(false)
    }
  }

  // Standalone keyboard: 1–9 picks, s accepts the suggested answer.
  const pickRef = useRef<(o: OptionView) => void>(() => {})
  const sendPick = (optionId: string) => {
    const merged: PickExtra = { ...(note.extra() ?? {}), ...board.extra() } // visual-review
    return Object.keys(merged).length ? p.onPick(optionId, merged) : p.onPick(optionId)
  }
  // A note or board comments in progress: a number key picks (marks) the option, and the button or
  // ⏎ in the note sends both. Otherwise a pick sends at once (1 key).
  pickRef.current = (o) => {
    if (note.started || board.dirty) {
      cur.setPicked(o.id)
      cur.setCursor(o.id)
    } else void act(() => sendPick(o.id))
  }
  // ---- one action list: keycaps, footer, status line, ⌘K and these keys all read it ----
  const hasTurns = !!p.turns
  const href = p.context.conversationHref
  const entity = useMemo<ActionEntity>(
    () => ({
      kind: 'decision',
      id: v.id,
      title: v.title,
      ...(v.runId ? { runId: v.runId } : {}),
      options: v.options.slice(0, 9).map((o) => ({
        n: o.key,
        label: o.label,
        ...(o.recommended ? { suggested: true } : {}),
      })),
    }),
    [v.id, v.title, v.runId, v.options],
  )
  const doVerb = useRef<(verb: string) => void>(() => {})
  doVerb.current = (verb) => {
    if (verb.startsWith('pick')) {
      const o = v.options.find((x) => x.key === Number(verb.slice(4)))
      if (o) pickRef.current(o)
    } else if (verb === 'suggested') {
      if (v.suggested) pickRef.current(v.suggested)
    } else if (verb === 'note' || verb === 'reply') {
      setCompose(verb === 'reply' && !v.options.length ? 'answer' : 'note')
      note.focus()
    } else if (verb === 'delegate') setMode('delegate')
    else if (verb === 'snooze') setMode('later')
    else if (verb === 'turns') setTurnsOpen((x) => !x)
    else if (verb === 'session' && href) void nav({ href })
  }
  const ctx = useMemo<ActionCtx>(
    () => ({
      do: (verb) => doVerb.current(verb),
      unavailable: (verb) =>
        verb === 'turns' && !hasTurns
          ? 'No turns to show for this ask.'
          : verb === 'session' && !href
            ? 'This ask is not tied to a session.'
            : verb === 'reply' && !v.freeform
              ? 'This ask takes one of its options.'
              : undefined,
    }),
    [hasTurns, href, v.freeform],
  )
  const list = useMemo(() => (v.open ? actions(entity, ctx) : []), [entity, ctx, v.open])
  useSelectionActions('decision', v.title, hot ? list : [], v.key)
  // n / v / ⇧V / r / d ride ahead of the global keys; 1–9 and s only when this view owns them
  // (a list owns them otherwise), and e stays with the list's snooze.
  useActionKeys(list, {
    enabled: hot,
    only: (a) => ['note', 'turns', 'session', 'reply', 'delegate'].includes(a.id),
  })
  useActionKeys(list, {
    enabled: !!p.keys && v.open,
    only: (a) => !!a.option || a.id === 'suggested',
    capture: false,
  })

  const noteRef = useRef(note)
  noteRef.current = note
  // Composer modes: a = Answer (your own words), b = New request; n (above) = Note.
  useEffect(() => {
    if (!hot) return
    const onKey = (e: KeyboardEvent) => {
      if ((e.key !== 'a' && e.key !== 'b') || e.metaKey || e.ctrlKey || e.altKey) return
      const t = e.target as HTMLElement | null
      if (t?.closest('input, textarea, select, [contenteditable="true"]')) return
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) return
      e.preventDefault()
      e.stopImmediatePropagation()
      setCompose(e.key === 'a' ? 'answer' : 'request')
      noteRef.current.focus()
    }
    addEventListener('keydown', onKey, true)
    return () => removeEventListener('keydown', onKey, true)
  }, [hot])

  // compact-blocker: ⏎ picks the focused option while focus rests in this decision's inspector
  // (its heading after opening), never from a control, a field or the page behind it. ↑/↓ move
  // the option cursor there (the list behind keeps j/k).
  const article = useRef<HTMLElement>(null)
  const enterPick = useRef<() => void>(() => {})
  enterPick.current = () => {
    const o = v.options.find((x) => x.id === selected)
    if (o) pickRef.current(o)
  }
  const stepRef = useRef(cur.step)
  stepRef.current = cur.step
  useEffect(() => {
    if (!hot) return
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return
      const arrow = e.key === 'ArrowDown' || e.key === 'ArrowUp'
      if (e.key !== 'Enter' && !arrow) return
      if (e.defaultPrevented || document.querySelector('[role="dialog"][aria-modal="true"]')) return
      const t = e.target as HTMLElement | null
      const host = article.current?.closest('[data-inspector]') ?? article.current
      if (!t || !host?.contains(t)) return
      if (
        t.closest('button, a, input, textarea, select, summary, [role="radio"], [contenteditable]')
      )
        return
      e.preventDefault()
      e.stopImmediatePropagation()
      if (arrow) stepRef.current(e.key === 'ArrowDown' ? 1 : -1)
      else enterPick.current()
    }
    addEventListener('keydown', onKey, true)
    return () => removeEventListener('keydown', onKey, true)
  }, [hot])

  const pending = busy || !!p.busy
  const options = v.options.length ? (
    <Options
      view={v}
      cur={cur}
      active={hot}
      onPick={(o) => pickRef.current(o)}
      disabled={!v.open || pending}
      board={board}
      {...(p.renderMedia ? { renderMedia: p.renderMedia } : {})}
    />
  ) : null
  // Phone (app-clarity mobile): the options, the note and the actions sit in the thumb zone above
  // the tab bar; the question and its context scroll above it. Option boards stay in the scroll.
  const isBoard = !!(
    v.board &&
    p.renderMedia &&
    (v.board.areas.length || v.options.some((o) => o.media.length))
  )
  const docked = !wide && v.open && !isBoard
  const bar = (part: BarPart) => (
    <AnswerBar
      key={`${v.key}:${part}`}
      part={part}
      {...(part === 'all' ? { docked: options } : {})}
      compose={compose}
      setCompose={(c) => {
        setCompose(c)
        note.focus()
      }}
      view={v}
      mode={mode}
      setMode={setMode}
      selected={v.options.find((o) => o.id === selected)}
      busy={pending}
      note={note}
      onPick={(o) =>
        act(async () => {
          await sendPick(o.id)
          note.clear()
          board.reset() // visual-review
        })
      }
      onFreeform={(t) =>
        act(async () => {
          await p.onFreeform(t)
          note.clear()
        })
      }
      onRequest={(t) => {
        ui.openCapture({ text: t })
        note.clear()
        setCompose('note')
      }}
      onDelegate={(g) => act(() => p.onDelegate(g))}
      onSnooze={(u) => act(() => p.onSnooze(u))}
      more={{
        ...(hasTurns ? { turns: { open: turnsOpen, toggle: () => setTurnsOpen((x) => !x) } } : {}),
        ...(href ? { sessionHref: href } : {}),
        command: `viko answer ${v.id} ${v.options.find((o) => o.id === selected)?.key ?? 1}`,
      }}
    />
  )
  return (
    <article
      ref={article}
      className="dv"
      data-owns-arrows={hot || undefined}
      aria-labelledby={`${id}-q`}
      data-decision={v.id}
      data-source={v.source}
    >
      <div className="dv__scroll">
        <header className="dv__head">
          {p.showTitle ? <h2 className="dv__title">{v.title}</h2> : null}
          <p id={`${id}-q`} className="dv__question">
            {v.question || 'Encrypted: unlock this browser to read'}
          </p>
          {/* compact-blocker: who, where, leverage and age in one muted line; the rest on demand */}
          <p className="dv__meta">
            <span>{p.context.agentShort ?? p.context.agentLabel ?? 'An agent'}</span>
            {p.context.projectName || p.context.lane ? (
              <span>{[p.context.projectName, p.context.lane].filter(Boolean).join(' › ')}</span>
            ) : null}
            <span>
              <Ago ts={v.createdAt} />
            </span>
            {v.risky ? <span className="is-risky">Needs your passkey</span> : null}
            <button
              type="button"
              className="dv-link dv__more"
              aria-expanded={details}
              onClick={() => setDetails(!details)}
            >
              <Info size={13} aria-hidden="true" />
              {details ? 'Less' : 'Details'}
            </button>
          </p>
          {v.why ? <p className={`dv__why${details ? '' : ' is-clamped'}`}>{v.why}</p> : null}
          {/* app-clarity carry-overs: how it got here in 2–3 lines, and the screenshot the agent
              marked directly relevant, large in the focus area (more context stays below). */}
          {v.keyPoints.length ? (
            <ul className="dv-points" aria-label="Key points">
              {v.keyPoints.map((k) => (
                <li key={`${k.label}:${k.text}`}>
                  <b>{k.label}</b> {k.text}
                </li>
              ))}
            </ul>
          ) : null}
          {(() => {
            const hero = v.evidence.find((e) => e.kind === 'artifact' && e.relevance === 'direct')
            return hero && p.renderArtifact ? (
              <figure className="dv-hero" data-evidence-hero>
                {p.renderArtifact(hero)}
                <figcaption>{hero.label || hero.ref}</figcaption>
              </figure>
            ) : null
          })()}
          {details ? (
            <dl className="dv__facts">
              <Fact k="Asked by">{p.context.agentLabel ?? 'An agent'}</Fact>
              {p.context.projectName || p.context.lane ? (
                <Fact k="Where">
                  {[p.context.projectName, p.context.lane].filter(Boolean).join(', ')}
                </Fact>
              ) : null}
              {p.context.task ? (
                <Fact k="Task">
                  {p.context.task.onOpen ? (
                    <button type="button" className="dv-link" onClick={p.context.task.onOpen}>
                      {p.context.task.title}
                    </button>
                  ) : (
                    p.context.task.title
                  )}
                </Fact>
              ) : null}
              <Fact k="Kind">{v.categoryLabel}</Fact>
              <Fact k="Asked">
                <Ago ts={v.createdAt} />
              </Fact>
              <Fact k="Waiting">{v.leverage.join(' · ')}</Fact>
              <Fact k="If you skip it">{v.skipText}</Fact>
            </dl>
          ) : null}
        </header>

        {!v.open ? <Outcome view={v} /> : null}

        {docked ? null : options}
        {v.open && !docked ? bar('compose') : null}

        {p.turns ? (
          <section className="dv-sec dv-led" aria-label="Turns that led here">
            {p.turns({ expanded: turnsOpen, setExpanded: setTurnsOpen })}
          </section>
        ) : null}

        <Evidence
          view={v}
          renderRef={p.renderRef}
          renderArtifact={p.renderArtifact}
          transcript={p.transcript}
          artifacts={p.artifacts}
          conversationRunId={p.turns ? undefined : p.context.conversationRunId}
        />

        <Thread
          view={v}
          composer={composer}
          onMessage={p.onMessage}
          conversationRunId={p.context.conversationRunId}
        />
      </div>

      {v.open && docked ? bar('all') : v.open ? bar('actions') : null}
    </article>
  )
}

function Fact({ k, children }: { k: string; children: ReactNode }) {
  return (
    <div>
      <dt>{k}</dt>
      <dd>{children}</dd>
    </div>
  )
}

function Outcome({ view: v }: { view: DecisionModel }) {
  const text =
    v.status === 'withdrawn'
      ? 'The agent withdrew this question.'
      : v.status === 'superseded'
        ? 'A newer question replaced this one.'
        : v.status === 'expired'
          ? 'This question expired.'
          : v.answerText
  return (
    <section className="dv-outcome" aria-label="Outcome">
      <p className="dv-outcome__line">
        <Check size={16} aria-hidden="true" /> {text ?? 'Answered'}
      </p>
      {v.status === 'delegated' ? (
        <p className="dv-outcome__sub">
          The agent is deciding. Its choice shows here as an assumption.
        </p>
      ) : null}
      {v.assumptionText ? (
        <div className="dv-assume">
          <Sparkles size={15} aria-hidden="true" />
          <div>
            <p className="dv-assume__h">Assumption</p>
            <p className="dv-assume__t">{v.assumptionText}</p>
          </div>
        </div>
      ) : null}
    </section>
  )
}

// ---- options -------------------------------------------------------------------------------------

function Options({
  view: v,
  cur,
  active,
  onPick,
  disabled,
  board,
  renderMedia,
}: {
  view: DecisionModel
  cur: OptionCursor
  /** This decision owns the keyboard: the cursor ring shows without DOM focus. */
  active: boolean
  onPick: (o: OptionView) => void
  disabled: boolean
  board: ReturnType<typeof useBoardState>
  renderMedia?: RenderMedia
}) {
  const hid = useId()
  const [diff, setDiff] = useState(false)
  // compact-blocker: an option's trade-offs show on demand (→, or a second click).
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set())
  const changes = useMemo(() => optionDiff(v), [v])
  const picked = v.decision?.answer?.optionId ?? v.decision?.resolution?.optionId
  return (
    <section className="dv-sec dv-sec--opts" aria-labelledby={hid}>
      <div className="dv-sec__h">
        <h3 id={hid} className="dv-sec__label">
          Options
        </h3>
        {v.revision.n > 0 ? (
          <span className="dv-rev">
            Revised {v.revision.at ? <Ago ts={v.revision.at} /> : null}
            {v.revision.previous ? (
              <button
                type="button"
                className="dv-link"
                aria-expanded={diff}
                onClick={() => setDiff(!diff)}
              >
                {diff ? 'Hide changes' : 'Show changes'}
              </button>
            ) : null}
          </span>
        ) : null}
      </div>
      {diff ? (
        <ul className="dv-diff" aria-label="What changed">
          {changes.added.map((l) => (
            <li key={`a${l}`} className="is-add">
              Added: {l}
            </li>
          ))}
          {changes.removed.map((l) => (
            <li key={`r${l}`} className="is-remove">
              Removed: {l}
            </li>
          ))}
          {changes.changed.map((l) => (
            <li key={`c${l}`}>Changed: {l}</li>
          ))}
          {!changes.added.length && !changes.removed.length && !changes.changed.length ? (
            <li>Same options, reworded.</li>
          ) : null}
        </ul>
      ) : null}
      {/* visual-review: options with images / HTML boards, or areas to mix, render as a board */}
      {v.board && renderMedia && (v.board.areas.length || v.options.some((o) => o.media.length)) ? (
        <OptionBoard
          view={v}
          cur={cur}
          active={active}
          onPick={onPick}
          disabled={disabled}
          board={board}
          renderMedia={renderMedia}
          labelledBy={hid}
        />
      ) : (
        <div className="dv-ballot" role="radiogroup" aria-labelledby={hid}>
          {v.options.map((o) => {
            const st = optionState(o, {
              cursor: cur.cursor,
              picked: v.open ? cur.picked : picked,
              active: active && v.open,
            })
            const on = st.picked
            const why = o.rationale || o.description
            const more = o.pros.length + o.cons.length > 0
            const open = expanded.has(o.id)
            const toggle = () =>
              setExpanded((cur) => {
                const next = new Set(cur)
                if (next.has(o.id)) next.delete(o.id)
                else next.add(o.id)
                return next
              })
            const mark = () => {
              cur.setPicked(o.id)
              cur.setCursor(o.id)
            }
            return (
              // biome-ignore lint/a11y/useSemanticElements: a rich option (rationale, trade-offs) is not a native radio label
              <div
                key={o.id}
                role="radio"
                aria-checked={on}
                aria-disabled={disabled || undefined}
                aria-describedby={more ? `${hid}-${o.key}-more` : undefined}
                tabIndex={disabled ? -1 : o.id === cur.cursor ? 0 : -1}
                className={`dv-opt ${st.className}${open ? ' is-open' : ''}`}
                data-option={o.key}
                data-option-id={o.id}
                onFocus={() => cur.setCursor(o.id)}
                onClick={() => {
                  if (disabled) return
                  // A second click on the picked option shows (or hides) its trade-offs.
                  if (on && (more || why)) toggle()
                  else mark()
                }}
                onDoubleClick={() => !disabled && onPick(o)}
                onKeyDown={(e) => {
                  if (disabled) return
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    onPick(o)
                  } else if (e.key === ' ') {
                    e.preventDefault()
                    mark()
                  } else if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
                    // → shows the option's trade-offs, ← hides them.
                    e.preventDefault()
                    if ((e.key === 'ArrowRight') !== open) toggle()
                  } else moveOnArrow(e, cur)
                }}
              >
                <OptionKey n={o.key} picked={on} />
                <div className="dv-opt__body">
                  <div className="dv-opt__top">
                    <span className="dv-opt__label">{o.label}</span>
                    {o.recommended ? <SuggestedPill /> : null}
                    {o.reversible === false ? (
                      <span className="dv-opt__flag is-oneway">Hard to undo</span>
                    ) : null}
                    {/* app-clarity: trade-offs are one key away (→ or a second click); the count
                        is announced, not drawn. */}
                    {more ? (
                      <span className="dv-opt__more sr-only" id={`${hid}-${o.key}-more`}>
                        <ChevronRight size={13} aria-hidden="true" />
                        <span>
                          {[
                            o.pros.length ? `${o.pros.length} for` : '',
                            o.cons.length ? `${o.cons.length} against` : '',
                          ]
                            .filter(Boolean)
                            .join(', ')}
                        </span>
                      </span>
                    ) : null}
                  </div>
                  {why ? <p className={`dv-opt__why${open ? '' : ' is-clamp'}`}>{why}</p> : null}
                  {open && more ? (
                    <div className="dv-opt__tradeoffs">
                      {o.pros.length ? (
                        <ul aria-label={`For ${o.label}`}>
                          {o.pros.map((x) => (
                            <li key={x} className="is-pro">
                              {x}
                            </li>
                          ))}
                        </ul>
                      ) : null}
                      {o.cons.length ? (
                        <ul aria-label={`Against ${o.label}`}>
                          {o.cons.map((x) => (
                            <li key={x} className="is-con">
                              {x}
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </section>
  )
}

// ---- evidence ------------------------------------------------------------------------------------

const EVIDENCE_ICON: Record<EvidenceView['kind'], ReactNode> = {
  artifact: <ImageIcon size={15} aria-hidden="true" />,
  diff: <FileDiff size={15} aria-hidden="true" />,
  test: <FlaskConical size={15} aria-hidden="true" />,
  commit: <GitCommitHorizontal size={15} aria-hidden="true" />,
  transcript: <ScrollText size={15} aria-hidden="true" />,
  link: <Link2 size={15} aria-hidden="true" />,
}
const EVIDENCE_KIND: Record<EvidenceView['kind'], string> = {
  artifact: 'Artifact',
  diff: 'Diff',
  test: 'Tests',
  commit: 'Commit',
  transcript: 'Conversation',
  link: 'Link',
}

function Evidence({
  view: v,
  renderRef,
  renderArtifact,
  transcript,
  artifacts,
  conversationRunId,
}: {
  view: DecisionModel
  renderRef: DecisionViewProps['renderRef']
  renderArtifact: DecisionViewProps['renderArtifact']
  transcript: ReactNode
  artifacts: ReactNode
  conversationRunId: string | undefined
}) {
  const hid = useId()
  const [showTx, setShowTx] = useState(false)
  const items = v.evidence.filter((e) => e.kind !== 'transcript')
  return (
    <section className="dv-sec" aria-labelledby={hid}>
      <div className="dv-sec__h">
        <h3 id={hid}>Artifacts &amp; assets</h3>
        {items.length ? <span className="dv-sec__note">{items.length}</span> : null}
      </div>
      {items.length ? (
        <ul className="dv-ev">
          {items.map((e) => (
            <li key={`${e.kind}:${e.ref}`} className="dv-ev__item" data-evidence={e.kind}>
              <div className="dv-ev__top">
                <span className={`dv-ev__icon${e.status ? ` is-${e.status}` : ''}`}>
                  {EVIDENCE_ICON[e.kind]}
                </span>
                <span className="dv-ev__kind">{EVIDENCE_KIND[e.kind]}</span>
                <span className="dv-ev__label">
                  {e.kind === 'link' ? (
                    <a href={e.ref} target="_blank" rel="noreferrer" className="dv-link">
                      {e.label || e.ref}
                    </a>
                  ) : e.kind === 'artifact' ? (
                    e.label || e.ref
                  ) : renderRef ? (
                    renderRef(e, e.label || e.ref)
                  ) : (
                    e.label || e.ref
                  )}
                </span>
                {e.status ? (
                  <span className={`dv-tag is-${e.status}`}>
                    {e.status === 'pass' ? 'Passing' : e.status === 'fail' ? 'Failing' : 'Running'}
                  </span>
                ) : null}
              </div>
              {e.kind !== 'link' && e.kind !== 'artifact' && e.label && e.ref !== e.label ? (
                <code className="dv-ev__ref">{e.ref}</code>
              ) : null}
              {e.excerpt ? <Excerpt kind={e.kind} text={e.excerpt} /> : null}
              {e.kind === 'artifact' && renderArtifact ? (
                <div className="dv-ev__preview">{renderArtifact(e)}</div>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      {!items.length && !transcript && !artifacts ? (
        <p className="dv-muted">
          Nothing was attached to this ask. Add screenshots or files with your answer below.
        </p>
      ) : null}
      {transcript ? (
        <div className="dv-tx">
          <button
            type="button"
            className="dv-link"
            aria-expanded={showTx}
            onClick={() => setShowTx(!showTx)}
          >
            <ScrollText size={14} aria-hidden="true" />
            {showTx
              ? 'Hide the conversation around this question'
              : 'Show the conversation around this question'}
          </button>
          {showTx ? <div className="dv-tx__embed">{transcript}</div> : null}
        </div>
      ) : null}
      {conversationRunId ? (
        <Link className="dv-link" to="/s/$runId" params={{ runId: conversationRunId }}>
          Open full conversation <ArrowRight size={13} aria-hidden="true" />
        </Link>
      ) : null}
      {artifacts ? <div className="dv-arts">{artifacts}</div> : null}
    </section>
  )
}

function Excerpt({ kind, text }: { kind: EvidenceView['kind']; text: string }) {
  const lines = text
    .split('\n')
    .slice(0, 40)
    .map((line, n) => ({ id: n, line }))
  const tone = (l: string) =>
    kind !== 'diff' ? '' : l.startsWith('+') ? 'is-add' : l.startsWith('-') ? 'is-remove' : ''
  return (
    <pre className="dv-ex" data-kind={kind}>
      {lines.map((x) => (
        <span key={x.id} className={tone(x.line)}>
          {x.line}
          {'\n'}
        </span>
      ))}
    </pre>
  )
}

// ---- thread --------------------------------------------------------------------------------------

const AUTHOR: Record<string, string> = {
  user: 'You',
  agent: 'Agent',
  triage: 'Triage agent',
  system: 'Viko',
}

function Thread({
  view: v,
  composer,
  onMessage,
  conversationRunId,
}: {
  view: DecisionModel
  composer: React.RefObject<HTMLTextAreaElement | null>
  onMessage: DecisionViewProps['onMessage']
  conversationRunId: string | undefined
}) {
  const hid = useId()
  const [draft, setDraft] = useState('')
  const [busy, setBusy] = useState(false)
  const input = useAttachmentInput({
    storageOff: storage.off,
    largeTextBytes: LARGE_TEXT_BYTES,
    insertText: (t) => setDraft((cur) => cur + t),
  })
  const dictation = useDictation((t) =>
    setDraft((cur) => (cur.trim() ? `${cur.replace(/\s+$/, '')} ${t}` : t)),
  )
  const send = async () => {
    const text = draft.trim()
    if (!onMessage || busy || (!text && !input.files.length)) return
    setBusy(true)
    input.markUploading(true)
    try {
      await onMessage(text || 'See the attachment.', input.files)
      setDraft('')
      input.clear()
    } finally {
      input.retryable()
      input.markUploading(false)
      setBusy(false)
    }
  }
  return (
    <section className="dv-sec" aria-labelledby={hid}>
      <div className="dv-sec__h">
        <h3 id={hid}>Conversation</h3>
        {v.threadEnabled ? (
          <span className="dv-sec__note">
            With {v.agent === 'codex' ? 'Codex' : 'the asking agent'}
          </span>
        ) : null}
      </div>
      {v.thread.length ? (
        <ol className="dv-thread" aria-live="polite">
          {v.thread.map((m) => (
            <li key={m.id} className={`dv-msg is-${m.author}`} data-message={m.id}>
              <div className="dv-msg__meta">
                <span className="dv-msg__who">{AUTHOR[m.author] ?? m.author}</span>
                <Ago ts={m.at} />
                {m.revisedOptions ? <span className="dv-tag">Revised the options</span> : null}
              </div>
              <div className="dv-msg__text">
                {m.author === 'user' ? (
                  <span className="whitespace-pre-wrap">{m.text}</span>
                ) : (
                  <Markdown text={m.text} />
                )}
              </div>
              {m.attachments.length ? (
                <p className="dv-msg__att">
                  {m.attachments.map((a: BarkAttachment) => a.name ?? 'Attachment').join(', ')}
                </p>
              ) : null}
              {m.author === 'user' ? (
                <p
                  className={`dv-msg__delivery${m.delivery?.status === 'failed' ? ' is-failed' : ''}`}
                  data-delivery={m.delivery?.status ?? 'sending'}
                >
                  {deliveryText(m.delivery)}
                </p>
              ) : null}
            </li>
          ))}
        </ol>
      ) : v.threadEnabled ? (
        <p className="dv-muted">
          Ask why, push back, or add a screenshot. The agent answers here and can revise the
          options.
        </p>
      ) : null}
      {v.threadEnabled && onMessage ? (
        <AttachmentDropZone input={input} className="dv-composer">
          <label className="sr-only" htmlFor={`${hid}-c`}>
            Message the agent about this decision
          </label>
          <textarea
            id={`${hid}-c`}
            ref={composer}
            rows={2}
            value={dictation.interim ? `${draft} ${dictation.interim}` : draft}
            placeholder="Ask why, push back, or suggest another option"
            onChange={(e) => setDraft(e.target.value)}
            onPaste={input.onPaste}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault()
                void send()
              }
              if (e.key === 'Escape') e.currentTarget.blur()
            }}
          />
          <div className="dv-composer__row">
            {dictation.supported ? (
              <Button
                size="sm"
                variant="ghost"
                aria-pressed={dictation.listening}
                aria-label={dictation.listening ? 'Stop dictation' : 'Dictate'}
                icon={dictation.listening ? <MicOff size={14} /> : <Mic size={14} />}
                onClick={dictation.toggle}
              />
            ) : null}
            <span className="dv-muted dv-composer__hint">Paste or drop screenshots and files</span>
            <Button
              size="sm"
              variant="secondary"
              icon={<Send size={14} />}
              disabled={busy || (!draft.trim() && !input.files.length)}
              onClick={() => void send()}
            >
              Send
            </Button>
          </div>
        </AttachmentDropZone>
      ) : !v.threadEnabled ? (
        <p className="dv-muted">
          This question came straight from the agent's session, so it has no thread.{' '}
          {conversationRunId ? (
            <Link className="dv-link" to="/s/$runId" params={{ runId: conversationRunId }}>
              Reply in the conversation
            </Link>
          ) : (
            'Reply in the conversation.'
          )}
        </p>
      ) : null}
    </section>
  )
}

// ---- answer bar ----------------------------------------------------------------------------------

type BarPart = 'compose' | 'actions' | 'all'

const COMPOSE: { id: Compose; label: string; key: string }[] = [
  { id: 'note', label: 'Note', key: 'n' },
  { id: 'answer', label: 'Answer', key: 'a' },
  { id: 'request', label: 'New request', key: 'b' },
]

/**
 * The answer area (design-language §7b, app-clarity "Composer modes"). `compose`: the mode
 * switch and the always-visible field (desktop: right under the options); `actions`: Pick ·
 * Decide for me · Later · ⋯ (desktop: the sticky footer); `all`: both, plus the docked options
 * (phone: the thumb zone above the tab bar).
 */
function AnswerBar({
  part,
  docked,
  compose,
  setCompose,
  view: v,
  mode,
  setMode,
  selected,
  busy,
  note,
  onPick,
  onFreeform,
  onRequest,
  onDelegate,
  onSnooze,
  more,
}: {
  part: BarPart
  /** Phone: the options, docked at the top of the zone. */
  docked?: ReactNode
  compose: Compose
  setCompose: (c: Compose) => void
  view: DecisionModel
  mode: Mode
  setMode: (m: Mode) => void
  /** The option a pick sends: the picked one, else the focused one. */
  selected: OptionView | undefined
  busy: boolean
  note: Note
  onPick: (o: OptionView) => Promise<void>
  onFreeform: (t: string) => Promise<void>
  onRequest: (t: string) => void
  onDelegate: (g?: string) => Promise<void>
  onSnooze: (until: number) => Promise<void>
  /** The overflow (…): turns, the session, the CLI command. */
  more: MoreProps
}) {
  const hid = useId()
  const wide = useWide()
  const [guidance, setGuidance] = useState('')
  const field = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (mode === 'delegate') field.current?.focus()
  }, [mode])
  const hasOptions = v.options.length > 0
  const text = note.text.trim()
  // The note alone can be the whole answer ("none of these: do X").
  const noteOnly = () => {
    if (text) void onFreeform(text)
  }
  const pickWithNote = () => {
    if (selected && !note.blocked) void onPick(selected)
  }
  const request = () => {
    if (text) onRequest(text)
  }
  const showCompose = part !== 'actions'
  const showActions = part !== 'compose'
  const modeSwitch = hasOptions ? (
    <div className="dv-modes" role="tablist" aria-label="What the field sends">
      {COMPOSE.map((c) => (
        <button
          key={c.id}
          type="button"
          role="tab"
          aria-selected={compose === c.id}
          aria-keyshortcuts={c.key}
          className="dv-modes__tab"
          onClick={() => setCompose(c.id)}
        >
          {c.label}
          <kbd className="ak">{c.key}</kbd>
        </button>
      ))}
    </div>
  ) : null
  const field_ =
    compose === 'note' && hasOptions ? (
      <NoteField
        note={note}
        target={selected?.label}
        busy={busy}
        onPickWithNote={pickWithNote}
        onNoteOnly={noteOnly}
      />
    ) : (
      <NoteField
        note={note}
        busy={busy}
        files={compose !== 'request'}
        placeholder={
          compose === 'request'
            ? wide
              ? 'Tell Viko anything: a new request…'
              : 'A new request…'
            : 'Write your answer…'
        }
        hint={
          compose === 'request'
            ? 'A new request; this question stays open'
            : hasOptions
              ? 'Sent instead of an option; the question closes'
              : 'Your answer, in your words'
        }
        onNoteOnly={compose === 'request' ? request : noteOnly}
      />
    )
  const panels =
    mode === 'delegate' ? (
      <div className="dv-bar__panel">
        <label htmlFor={`${hid}-g`} className="dv-bar__label">
          Let the agent decide. Any guidance?
        </label>
        <input
          id={`${hid}-g`}
          ref={field}
          value={guidance}
          placeholder="Prefer the simpler one; keep it offline"
          onChange={(e) => setGuidance(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              void onDelegate(guidance.trim() || undefined)
            }
            if (e.key === 'Escape') setMode('pick')
          }}
        />
        <p className="dv-muted">
          It picks, keeps going, and reports its choice as an assumption you can change.
        </p>
        <div className="dv-bar__row">
          <Button size="sm" variant="ghost" onClick={() => setMode('pick')}>
            Back
          </Button>
          <Button
            size="sm"
            variant="primary"
            disabled={busy}
            onClick={() => void onDelegate(guidance.trim() || undefined)}
          >
            Let the agent decide
          </Button>
        </div>
      </div>
    ) : mode === 'later' ? (
      <div className="dv-bar__panel">
        <p className="dv-bar__label" id={`${hid}-l`}>
          Remind me
        </p>
        <div className="dv-bar__row" role="group" aria-labelledby={`${hid}-l`}>
          {SNOOZE_OPTIONS.map((o) => (
            <Button
              key={o.label}
              size="sm"
              variant="secondary"
              disabled={busy}
              onClick={() => void onSnooze(snoozeUntil(o.ms))}
            >
              {o.label}
            </Button>
          ))}
          <Button size="sm" variant="ghost" onClick={() => setMode('pick')}>
            Back
          </Button>
        </div>
      </div>
    ) : null
  const primary =
    compose === 'note' && hasOptions ? (
      <Button
        variant="primary"
        size="sm"
        className="dv-bar__pick"
        title={selected?.label}
        disabled={busy || !selected || note.blocked}
        onClick={pickWithNote}
      >
        <span className="dv-bar__label">
          {selected
            ? `Pick ${selected.label}${note.started ? ' with note' : ''}`
            : 'Pick an option'}
        </span>
        {selected ? (
          <span aria-hidden="true">
            <Keycap>⏎</Keycap>
          </span>
        ) : null}
      </Button>
    ) : (
      <Button
        variant="primary"
        size="sm"
        className="dv-bar__pick"
        aria-keyshortcuts="Meta+Enter"
        disabled={busy || !text || note.blocked}
        onClick={compose === 'request' ? request : noteOnly}
      >
        <span className="dv-bar__label">
          {compose === 'request' ? 'Send as a request' : 'Send answer'}
        </span>
        <span aria-hidden="true">
          <Keycap>⌘↵</Keycap>
        </span>
      </Button>
    )
  const noteOnlyBtn =
    compose === 'note' && hasOptions && note.started && text ? (
      <Button
        size="sm"
        variant="ghost"
        aria-keyshortcuts="Meta+Enter"
        disabled={busy}
        onClick={noteOnly}
      >
        Send note only <Keycap>⌘↵</Keycap>
      </Button>
    ) : null
  const secondary = (
    <>
      <Button size="sm" variant="ghost" aria-keyshortcuts="d" onClick={() => setMode('delegate')}>
        Decide for me <Keycap>d</Keycap>
      </Button>
      <Button size="sm" variant="ghost" aria-keyshortcuts="e" onClick={() => setMode('later')}>
        Later <Keycap>e</Keycap>
      </Button>
      <MoreMenu {...more} />
    </>
  )
  if (part === 'compose')
    return (
      <section className="dv-compose" aria-label="What you send">
        {panels ?? (
          <>
            {modeSwitch}
            {hasOptions || v.freeform ? field_ : null}
          </>
        )}
      </section>
    )
  return (
    <section className={`dv-bar${part === 'all' ? ' dv-bar--zone' : ''}`} aria-label="Answer">
      {showCompose && !panels ? docked : null}
      {showCompose && panels ? panels : null}
      {showCompose && !panels ? modeSwitch : null}
      {showCompose && !panels && (hasOptions || v.freeform) ? field_ : null}
      {showActions && !panels ? (
        part === 'all' ? (
          <>
            <div className="dv-bar__row dv-bar__row--send">
              {noteOnlyBtn}
              {primary}
            </div>
            <div className="dv-bar__row dv-bar__row--quiet">{secondary}</div>
          </>
        ) : (
          <div className="dv-bar__row">
            {primary}
            {noteOnlyBtn}
            {secondary}
          </div>
        )
      ) : null}
    </section>
  )
}

// ---- compact-blocker: the overflow (…) ------------------------------------------------------------

interface MoreProps {
  turns?: { open: boolean; toggle: () => void }
  sessionHref?: string
  /** `viko answer <id> <n>` for the selected option (copied in full; never shown inline). */
  command: string
}

/** Secondary verbs: show turns (v), open in session (⇧V), copy the CLI command. A disclosure
 * anchored to its button (never a bottom sheet); Esc or a pick closes it. */
function MoreMenu({ turns, sessionHref, command }: MoreProps) {
  const [open, setOpen] = useState(false)
  const id = useId()
  const toast = useToast()
  const box = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const onDown = (e: PointerEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        e.stopPropagation()
        setOpen(false)
      }
    }
    addEventListener('pointerdown', onDown)
    addEventListener('keydown', onKey, true)
    return () => {
      removeEventListener('pointerdown', onDown)
      removeEventListener('keydown', onKey, true)
    }
  }, [open])
  return (
    <div className="dv-more" ref={box}>
      <button
        type="button"
        className="vk-iconbtn dv-more__btn"
        aria-label="More actions"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen(!open)}
      >
        <MoreHorizontal size={18} aria-hidden="true" />
      </button>
      {open ? (
        <ul className="dv-more__list" id={id} aria-label="More actions">
          {turns ? (
            <li>
              <button
                type="button"
                onClick={() => {
                  turns.toggle()
                  setOpen(false)
                }}
              >
                <span>{turns.open ? 'Hide turns' : 'Show turns'}</span>
                <Keycap>v</Keycap>
              </button>
            </li>
          ) : null}
          {sessionHref ? (
            <li>
              <Link to={sessionHref} onClick={() => setOpen(false)}>
                <span>Open in session</span>
                <ArrowRight size={13} aria-hidden="true" />
                <Keycap>⇧V</Keycap>
              </Link>
            </li>
          ) : null}
          <li>
            <button
              type="button"
              title={command}
              onClick={() => {
                setOpen(false)
                void navigator.clipboard?.writeText(command).then(
                  () => toast('Command copied'),
                  () => toast('Copy failed. Use the status line instead.', 'error'),
                )
              }}
            >
              <Copy size={13} aria-hidden="true" />
              <span>Copy CLI command</span>
            </button>
          </li>
        </ul>
      ) : null}
    </div>
  )
}
